import { useState, useEffect } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useAuthStore } from '../store/auth';
import { AuthShell, AuthCard, AuthInput, AuthButton, AuthError } from '../components/AuthShell';

const REFERRAL_KEY = 'squishy_pending_referral';

export function SignUpPage() {
  const nav = useNavigate();
  const [searchParams] = useSearchParams();
  const { signUp, loading, error, clearError } = useAuthStore();
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const returnUrl = searchParams.get('returnUrl') ?? '';
  const joinMatch = returnUrl.match(/\/groups\/join\/([A-Z0-9]+)/i);
  const joinRoomCode = joinMatch?.[1] ?? null;

  // Capture referral param from URL and persist in localStorage
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) localStorage.setItem(REFERRAL_KEY, ref);
  }, [searchParams]);

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    clearError();
    const r = await signUp(email.trim().toLowerCase(), password, firstName.trim(), lastName.trim());
    if (r.ok) {
      if (r.needsConfirm) nav('/confirm', { state: { email, returnUrl: returnUrl || undefined } });
      else nav(returnUrl || '/');
    }
  };

  return (
    <AuthShell>
      <AuthCard
        title={joinRoomCode ? 'Sign up to join' : 'Create your account'}
        subtitle={joinRoomCode ? undefined : 'Free forever for students. No credit card.'}
      >
        {joinRoomCode && (
          <div className="mb-4 bg-teal-50 border border-teal-200 rounded-xl p-3 text-center">
            <div className="text-xs text-teal-600 font-medium mb-1">You've been invited to a study group</div>
            <div className="text-lg font-mono font-bold text-squishy-dark tracking-[0.2em]">{joinRoomCode}</div>
            <div className="text-xs text-slate-500 mt-1">Create an account and you'll join automatically</div>
          </div>
        )}
        <form onSubmit={submit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <AuthInput label="First name" value={firstName} onChange={setFirstName} autoComplete="given-name" required autoFocus />
            <AuthInput label="Last name"  value={lastName}  onChange={setLastName}  autoComplete="family-name" required />
          </div>
          <AuthInput type="email" label="Email" value={email} onChange={setEmail} autoComplete="email" required />
          <AuthInput
            type="password" label="Password" value={password} onChange={setPassword}
            autoComplete="new-password" required
            placeholder="At least 10 chars, with upper/lower/number"
          />
          {error && <AuthError message={error} />}
          <AuthButton type="submit" loading={loading}>Create account</AuthButton>
        </form>
        <p className="mt-5 text-center text-sm text-slate-500">
          Already have an account? <Link to={`/login${returnUrl ? `?returnUrl=${encodeURIComponent(returnUrl)}` : ''}`} className="text-squishy-primary hover:underline">Sign in</Link>
        </p>
        <p className="mt-3 text-center text-xs text-slate-400">
          By creating an account, you agree to our <Link to="/privacy" className="hover:text-squishy-primary hover:underline">Privacy Policy</Link>.
        </p>
      </AuthCard>
    </AuthShell>
  );
}
